import { useState, useEffect } from 'react';
import axiosClient from './lib/axiosClient';

const TRACKING_MODES = ['individual', 'quantity'];
const PRODUCT_TYPES = ['rental', 'sell', 'both'];
const FIELD_TYPE_OPTIONS = ['text', 'number', 'select', 'boolean'];

const emptyGroup = {
  name: '',
  description: '',
  category_id: '',
  tracking_mode: 'individual',
  product_type: 'rental',
  base_price_hour: '',
  base_price_day: '',
  base_price_week: '',
  base_price_month: '',
  sale_price: '',
  base_deposit: '',
  tax_override: '',
  is_published: true,
  property_schema: [],
};

export default function ProductGroupEditor({ groupId, categories = [], defaultCategoryId = null, onSaved, onCancel }) {
  const [form, setForm] = useState({ ...emptyGroup, category_id: defaultCategoryId ? String(defaultCategoryId) : '' });

  useEffect(() => {
    if (defaultCategoryId && !groupId) {
      setForm(f => ({ ...f, category_id: String(defaultCategoryId) }));
    }
  }, [defaultCategoryId, groupId]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const isEdit = Boolean(groupId);
  const [cats, setCats] = useState(categories);
  const [newCatName, setNewCatName] = useState('');
  const [addingCat, setAddingCat] = useState(false);
  const [savingCat, setSavingCat] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingPhoto(true);
    try {
      const { createClient } = await import('@supabase/supabase-js');
      const sb = createClient(
        process.env.REACT_APP_SUPABASE_URL,
        process.env.REACT_APP_SUPABASE_ANON_KEY
      );
      const ext = file.name.split('.').pop();
      const filePath = 'products/' + Date.now() + '-' + Math.random().toString(36).slice(2) + '.' + ext;
      const { error } = await sb.storage.from('rental-photos').upload(filePath, file);
      if (error) throw error;
      const { data: { publicUrl } } = sb.storage.from('rental-photos').getPublicUrl(filePath);
      set('photo_urls', [...(form.photo_urls || []), publicUrl]);
    } catch(e) {
      setError('Photo upload failed: ' + (e.message || 'unknown error'));
    } finally {
      setUploadingPhoto(false);
    }
  };

  const removePhoto = (url) => {
    set('photo_urls', (form.photo_urls || []).filter(u => u !== url));
  };

  useEffect(() => {
    axiosClient.get('/api/rentals/categories')
      .then(r => setCats(r.data.categories || r.data))
      .catch(() => {});
  }, []);

  const handleAddCategory = async () => {
    if (!newCatName.trim()) return;
    setSavingCat(true);
    try {
      const r = await axiosClient.post('/api/rentals/categories', { name: newCatName.trim() });
      const created = r.data.category;
      setCats(prev => [...prev, created]);
      set('category_id', String(created.id));
      setNewCatName('');
      setAddingCat(false);
    } catch (e) {
      setError(e?.response?.data?.error || 'Failed to create category.');
    } finally {
      setSavingCat(false);
    }
  };

  useEffect(() => {
    if (groupId) {
      setLoading(true);
      axiosClient.get(`/api/rentals/products/${groupId}`)
        .then(r => {
          const g = r.data.product || r.data;
          setForm({
            name: g.name || '',
            description: g.description || '',
            category_id: g.category_id != null ? String(g.category_id) : '',
            tracking_mode: g.tracking_mode || 'individual',
            product_type: g.product_type || 'rental',
            base_price_hour: g.base_price_hour ?? '',
            base_price_day: g.base_price_day ?? '',
            base_price_week: g.base_price_week ?? '',
            base_price_month: g.base_price_month ?? '',
            sale_price: g.sale_price ?? '',
            base_deposit: g.base_deposit ?? '',
            tax_override: g.tax_override ?? '',
            is_published: g.is_published ?? true,
            property_schema: g.property_schema || [],
          });
        })
        .catch(() => setError('Failed to load product group.'))
        .finally(() => setLoading(false));
    }
  }, [groupId]);

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  // Category custom fields use {key,label,base_type/type_name,options}; map to this editor's simpler {key,label,type,options}
  const mapCatFieldType = (cf) => {
    const t = cf.base_type || cf.type || 'text';
    if (t === 'select' || t === 'multiselect') return 'select';
    if (t === 'number' || t === 'currency' || t === 'rating') return 'number';
    if (t === 'boolean') return 'boolean';
    return 'text';
  };

  // New products: mirror the category's custom field schema so Alex sees what
  // to fill in, instead of an empty list. Only fires while property_schema is
  // still empty, so it never clobbers fields already added/edited, or an
  // existing product's own saved schema.
  useEffect(() => {
    if (isEdit || !form.category_id || form.property_schema.length > 0) return;
    const cat = cats.find(c => String(c.id) === String(form.category_id));
    const catSchema = cat?.custom_field_schema || [];
    if (catSchema.length === 0) return;
    setForm(f => ({
      ...f,
      property_schema: catSchema.map(cf => ({
        key: cf.key || '',
        label: cf.label || cf.key || '',
        type: mapCatFieldType(cf),
        options: cf.options || [],
      })),
    }));
  }, [form.category_id, cats, isEdit]);

  const addField = () => setForm(f => ({
    ...f,
    property_schema: [...f.property_schema, { key: '', label: '', type: 'text', options: [] }],
  }));
  const updateField = (i, key, val) => setForm(f => {
    const schema = [...f.property_schema];
    schema[i] = { ...schema[i], [key]: val };
    return { ...f, property_schema: schema };
  });
  const removeField = i => setForm(f => ({
    ...f,
    property_schema: f.property_schema.filter((_, idx) => idx !== i),
  }));

  const handleSave = async () => {
    if (!form.name.trim()) { setError('Name is required.'); return; }
    setSaving(true);
    setError(null);
    const payload = {
      ...form,
      base_price_hour: form.base_price_hour === '' ? null : parseFloat(form.base_price_hour),
      base_price_day: form.base_price_day === '' ? null : parseFloat(form.base_price_day),
      base_price_week: form.base_price_week === '' ? null : parseFloat(form.base_price_week),
      base_price_month: form.base_price_month === '' ? null : parseFloat(form.base_price_month),
      sale_price: form.sale_price === '' ? null : parseFloat(form.sale_price),
      base_deposit: form.base_deposit === '' ? null : parseFloat(form.base_deposit),
      tax_override: form.tax_override === '' ? null : form.tax_override,
      category_id: form.category_id === '' ? null : form.category_id,
    };
    try {
      let result;
      if (isEdit) {
        result = await axiosClient.patch(`/api/rentals/products/${groupId}`, payload);
      } else {
        result = await axiosClient.post('/api/rentals/products', payload);
      }
      onSaved && onSaved(result.data);
    } catch (e) {
      setError(e?.response?.data?.error || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div style={styles.loading}>Loading…</div>;

  const showSalePrice = form.product_type === 'sell' || form.product_type === 'both';
  const showRentalPrices = form.product_type === 'rental' || form.product_type === 'both';

  return (
    <div style={styles.wrap}>
      <div style={styles.header}>
        <span style={styles.headerTitle}>{isEdit ? 'Edit Product Group' : 'New Product Group'}</span>
        <button onClick={onCancel} style={styles.cancelBtn}>✕</button>
      </div>

      {error && <div style={styles.error}>{error}</div>}

      <section style={styles.section}>
        <div style={styles.sectionLabel}>PRODUCT INFO</div>
        <div style={styles.row}>
          <label style={styles.label}>Name *</label>
          <input style={styles.input} value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Concrete Grinder 7in" />
        </div>
        <div style={styles.row}>
          <label style={styles.label}>Description</label>
          <textarea style={{ ...styles.input, height: 64, resize: 'vertical' }} value={form.description} onChange={e => set('description', e.target.value)} placeholder="Optional description" />
        </div>
        <div style={styles.row}>
          <label style={styles.label}>Category</label>
          <div style={{ display: 'flex', gap: 8 }}>
            <select key={cats.length} style={{ ...styles.select, flex: 1 }} value={form.category_id} onChange={e => set('category_id', e.target.value)}>
              <option value="">— None —</option>
              {cats.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
            <button type="button" onClick={() => setAddingCat(v => !v)} style={styles.addFieldBtn}>
              {addingCat ? '✕' : '+ New'}
            </button>
          </div>
          {addingCat && (
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <input
                style={{ ...styles.input, flex: 1 }}
                placeholder="Category name"
                value={newCatName}
                onChange={e => setNewCatName(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleAddCategory()}
                autoFocus
              />
              <button type="button" onClick={handleAddCategory} disabled={savingCat || !newCatName.trim()} style={styles.saveBtn}>
                {savingCat ? '...' : 'Add'}
              </button>
            </div>
          )}
        </div>
        <div style={styles.twoCol}>
          <div style={styles.row}>
            <label style={styles.label}>Tracking Mode</label>
            <select style={styles.select} value={form.tracking_mode} onChange={e => set('tracking_mode', e.target.value)}>
              {TRACKING_MODES.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <div style={styles.row}>
            <label style={styles.label}>Product Type</label>
            <select style={styles.select} value={form.product_type} onChange={e => set('product_type', e.target.value)}>
              {PRODUCT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
        </div>
      </section>

      <section style={styles.section}>
        <div style={styles.sectionLabel}>PRICING</div>
        {showRentalPrices && (
          <div style={styles.priceGrid}>
            {[['Hourly', 'base_price_hour'], ['Daily', 'base_price_day'], ['Weekly', 'base_price_week'], ['Monthly', 'base_price_month']].map(([label, key]) => (
              <div key={key} style={styles.row}>
                <label style={styles.label}>{label}</label>
                <div style={styles.inputPrefix}>
                  <span style={styles.prefix}>$</span>
                  <input style={{ ...styles.input, paddingLeft: 24 }} type="number" min="0" step="0.01" value={form[key]} onChange={e => set(key, e.target.value)} placeholder="0.00" />
                </div>
              </div>
            ))}
          </div>
        )}
        {showSalePrice && (
          <div style={styles.row}>
            <label style={styles.label}>Sale Price</label>
            <div style={styles.inputPrefix}>
              <span style={styles.prefix}>$</span>
              <input style={{ ...styles.input, paddingLeft: 24 }} type="number" min="0" step="0.01" value={form.sale_price} onChange={e => set('sale_price', e.target.value)} placeholder="0.00" />
            </div>
          </div>
        )}
        <div style={styles.row}>
          <label style={styles.label}>Deposit</label>
          <div style={styles.inputPrefix}>
            <span style={styles.prefix}>$</span>
            <input style={{ ...styles.input, paddingLeft: 24 }} type="number" min="0" step="0.01" value={form.base_deposit} onChange={e => set('base_deposit', e.target.value)} placeholder="0.00" />
          </div>
        </div>
        <div style={styles.row}>
          <label style={styles.label}>Tax</label>
          <select style={styles.select} value={form.tax_override} onChange={e => set('tax_override', e.target.value)}>
            <option value="">Business default for this product type</option>
            <option value="non_taxable">Never taxed</option>
            <option value="taxed_as_sale">Taxed as a sale</option>
          </select>
          <div style={{ fontSize: 11, color: '#777', marginTop: 4 }}>Taxability defaults are set in Settings → Tax. A rental product must have a day rate above $0 to be published.</div>
        </div>
      </section>

      <section style={styles.section}>
        <div style={{ ...styles.sectionLabel, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {/* PHOTOS */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1, color: '#555', textTransform: 'uppercase', marginBottom: 10 }}>Photos</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
              {(form.photo_urls || []).map((url, i) => (
                <div key={i} style={{ position: 'relative', width: 80, height: 80 }}>
                  <img src={url} alt="" style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 6, border: '1px solid #2a2a4a' }} />
                  <button onClick={() => removePhoto(url)} style={{ position: 'absolute', top: -6, right: -6, background: '#f87171', border: 'none', borderRadius: '50%', width: 18, height: 18, cursor: 'pointer', fontSize: 10, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
                </div>
              ))}
              <label style={{ width: 80, height: 80, border: '1px dashed #2a2a4a', borderRadius: 6, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#555', fontSize: 11, gap: 4 }}>
                {uploadingPhoto ? '...' : <><span style={{ fontSize: 20 }}>+</span><span>Photo</span></>}
                <input type="file" accept="image/*" style={{ display: 'none' }} onChange={handlePhotoUpload} disabled={uploadingPhoto} />
              </label>
            </div>
          </div>
          <span>CUSTOM FIELDS (PROPERTY SCHEMA)</span>
          <button onClick={addField} style={styles.addFieldBtn}>+ Add Field</button>
        </div>
        {form.property_schema.length === 0 && (
          <div style={styles.emptyFields}>No custom fields. These become properties tracked per variant.</div>
        )}
        {form.property_schema.map((field, i) => (
          <div key={i} style={styles.fieldRow}>
            <input style={{ ...styles.input, flex: 1 }} placeholder="key (e.g. color)" value={field.key} onChange={e => updateField(i, 'key', e.target.value)} />
            <input style={{ ...styles.input, flex: 1 }} placeholder="label (e.g. Color)" value={field.label} onChange={e => updateField(i, 'label', e.target.value)} />
            <select style={{ ...styles.select, flex: '0 0 100px' }} value={field.type} onChange={e => updateField(i, 'type', e.target.value)}>
              {FIELD_TYPE_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            {field.type === 'select' && (
              <input style={{ ...styles.input, flex: 1 }} placeholder="options (comma separated)" value={Array.isArray(field.options) ? field.options.join(', ') : ''} onChange={e => updateField(i, 'options', e.target.value.split(',').map(s => s.trim()).filter(Boolean))} />
            )}
            <button onClick={() => removeField(i)} style={styles.removeFieldBtn}>✕</button>
          </div>
        ))}
      </section>

      <div style={styles.publishRow}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
          <input type="checkbox" checked={form.is_published} onChange={e => set('is_published', e.target.checked)} />
          <span style={{ color: '#ccc', fontSize: 14 }}>Published (visible in storefront)</span>
        </label>
      </div>

      <div style={styles.actions}>
        <button onClick={onCancel} style={styles.secondaryBtn} disabled={saving}>Cancel</button>
        <button onClick={handleSave} style={styles.saveBtn} disabled={saving}>
          {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Product'}
        </button>
      </div>
    </div>
  );
}

const styles = {
  wrap: { background: '#252840', border: '1px solid #2a2a4a', borderRadius: 10, padding: 24, maxWidth: 720, color: '#e0e0f0', fontFamily: 'sans-serif' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 },
  headerTitle: { fontSize: 18, fontWeight: 700, color: '#fff' },
  cancelBtn: { background: 'none', border: 'none', color: '#888', fontSize: 18, cursor: 'pointer' },
  section: { marginBottom: 20, paddingBottom: 20, borderBottom: '1px solid #2a2a4a' },
  sectionLabel: { fontSize: 11, fontWeight: 700, letterSpacing: 1.2, color: '#6c6c9a', marginBottom: 12 },
  row: { marginBottom: 12 },
  twoCol: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 },
  priceGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 },
  label: { display: 'block', fontSize: 12, color: '#aaa', marginBottom: 4 },
  input: { width: '100%', background: '#0f0f20', border: '1px solid #3a3a5a', borderRadius: 6, padding: '8px 10px', color: '#e0e0f0', fontSize: 14, boxSizing: 'border-box' },
  select: { width: '100%', background: '#0f0f20', border: '1px solid #3a3a5a', borderRadius: 6, padding: '8px 10px', color: '#e0e0f0', fontSize: 14 },
  inputPrefix: { position: 'relative' },
  prefix: { position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: '#6c6c9a', fontSize: 14 },
  fieldRow: { display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center', flexWrap: 'wrap' },
  addFieldBtn: { background: '#2a2a5a', border: 'none', color: '#a0a0ff', borderRadius: 6, padding: '4px 10px', fontSize: 12, cursor: 'pointer' },
  removeFieldBtn: { background: 'none', border: 'none', color: '#e05555', cursor: 'pointer', fontSize: 14, flexShrink: 0 },
  emptyFields: { color: '#666', fontSize: 13, fontStyle: 'italic', padding: '8px 0' },
  publishRow: { marginBottom: 20 },
  actions: { display: 'flex', justifyContent: 'flex-end', gap: 10 },
  saveBtn: { background: '#4a4aff', border: 'none', color: '#fff', borderRadius: 6, padding: '10px 22px', fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  secondaryBtn: { background: '#2a2a4a', border: 'none', color: '#aaa', borderRadius: 6, padding: '10px 22px', fontSize: 14, cursor: 'pointer' },
  error: { background: '#3a1a1a', border: '1px solid #a03030', color: '#ff8080', borderRadius: 6, padding: '10px 14px', marginBottom: 14, fontSize: 13 },
  loading: { color: '#aaa', padding: 20 },
};
